import React, { useEffect, useState } from 'react';
import Scroll from 'react-scroll';
import PropTypes from 'prop-types';
import clone from 'lodash/clone';
import isArray from 'lodash/isArray';
import { Event as GaEvent } from '../../../../components/Tracking';
import MultistepForm, {
  MultistepFormPage,
} from '../../../Form/components/MultistepForm';
import { CSSTransition } from 'react-transition-group';
import './styles.css';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import ERROR_FIELD from '../../../../constants/error-fields';
import useApi from '../../../../hooks/useApi';
import BasicDialog from '../../../Dialog/components/BasicDialog';
import ResponseErrorDialog from '../../../Dialog/components/ResponseErrorDialog';
import FormStep1 from './components/FormStep1';
import FormStep2 from './components/FormStep2';
import FormStep3 from './components/FormStep3';
import FormStep4 from './components/FormStep4';
import CandidateRegistrationFormValidationSchema from './validate';

const CandidateRegistrationForm = props => {
  const { onAfterSaveFormHandler } = props;

  const api = useApi();

  const [step, setStep] = useState(0);

  const [initialValues, setInitialValues] = useState({
    isFetched: true,
    guestResume: null,
    isRecruiter: false,
    fields: {
      // step 1
      bs: [],
      fs: [],
      loc: [],
      lang: [],
      citizenship: [],
      manageAccount: null,
      openOp: null,

      // step 2
      role: null,
      title: null,
      degree: null,
      school: null,
      startDate: null,
      graduationYear: null,
      major: '',
      gpa: null,
      mathSat: null,
      verbalSat: null,
      writingSat: null,
      actScore: null,
      gmat: null,
      currentFirm: null,
      currentSchool: null,
      group: [],
      industry: [],
      officeLoc: null,

      // step 3
      resume: null,
      picture: null,

      // step 4
      workEmail: '',
      email2: '',
      phone: '',
      phone2: '',
    },
    options: {
      // step 1
      bs: [],
      loc: [],
      fs: [],
      citizenship: [],
      lang: [],
      langLevel: [],

      // step 2
      role: [],
      title: [],
      degree: [],
      graduationYear: [],
      workStart: { month: [], year: [] },
      gpa: [],
      mathSat: [],
      verbalSat: [],
      writingSat: [],
      actScore: [],
      gmat: [],
      group: [],
      industry: [],
      currentSchool: [],
    },
  });

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.CANDIDATE_ACCOUNT_REGISTRATION,
      {},
      {
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: data => {
          setInitialValues({
            isFetching: false,
            ...initialValues,
            ...data,
            fields: {
              ...initialValues.fields,
              ...data.fields,
              bs: data.options.bs
                .filter(record => data.fields.bs.indexOf(record.value) !== -1)
                .map(record => ({
                  ...record,
                  id: record.value,
                })),
              fs: data.options.fs
                .filter(record => data.fields.fs.indexOf(record.value) !== -1)
                .map(record => ({
                  ...record,
                  id: record.value,
                })),
              loc: data.options.loc
                .filter(record => data.fields.loc.indexOf(record.value) !== -1)
                .map(record => ({
                  ...record,
                  id: record.value,
                })),
            },
            options: {
              ...initialValues.options,
              ...data.options,
            },
          });
        },
      }
    );
  }, []);

  const prepareValues = values => {
    const preparedValues = clone(values);
    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (data === null || (isArray(data) && !data.length)) {
        delete preparedValues[field];
      } else if (field === 'lang') {
        data.forEach((value, index) => {
          preparedValues[`lang${index + 1}`] = value.language
            ? value.language.value
            : 0;
          preparedValues[`langLevel${index + 1}`] = value.level
            ? value.level.value
            : 0;
        });
        delete preparedValues[field];
      } else if (field === 'startDate') {
        preparedValues.month = data.month;
        preparedValues.year = data.year;
        delete preparedValues[field];
      } else if (field === 'picture') {
        if (data.avatar && data.avatar.Picture) {
          preparedValues[field] = data.avatar.Picture;
          preparedValues['x'] = data.avatar.x;
          preparedValues['y'] = data.avatar.y;
          preparedValues['width'] = data.avatar.width;
        } else {
          delete preparedValues[field];
        }
      } else if (field === 'resume') {
        preparedValues[field] = data;
      } else if (isArray(data)) {
        preparedValues[field] = data.map(item => item.value);
      } else if (typeof data === 'object') {
        if (data.__isNew__) {
          preparedValues[field] = -2;
          preparedValues[`${field}Other`] = data.label;
        } else {
          preparedValues[field] = data.value;
        }
      }
    });

    return preparedValues;
  };

  const handleError = (response, setErrors) => {
    const {
      data: { fields, step },
    } = response;
    if (fields && typeof setErrors === 'function') {
      const obj = {};
      for (let [key, value] of Object.entries(fields)) {
        const error = isArray(value) ? value[0] : value;
        obj[key] = ERROR_FIELD[error] || `${error}`;
        if (['month', 'year'].indexOf(key) !== -1) {
          obj['startDate'] = obj[key];
          delete obj[key];
        }
      }
      setErrors(obj);

      if (step) {
        changeStep(step - 1);
      }
    } else {
      ResponseErrorDialog({ response });
    }
  };

  const handleSubmitForm = (values, { setSubmitting, setErrors }) => {
    const params = prepareValues(values);
    setSubmitting(true);
    api.post(ENDPOINT.FORM.SAVE.CANDIDATE_ACCOUNT_REGISTRATION, params, {
      handleError: response => handleError(response, setErrors),
      handleSuccess: () => {
        BasicDialog({
          bodyRender: (
            <p style={{ maxWidth: '400px', whiteSpace: 'pre-wrap' }}>
              {'Thank you for completing your full profile.\n' +
                'Our team will now review your submission.\n' +
                'You will receive a welcome email to the platform once your account is activated.'}
            </p>
          ),
        });
        onAfterSaveFormHandler();
      },
      handleComplete: () => setSubmitting(false),
    });
  };

  const getUndergraduateSchoolOptions = (query, offset) => {
    return new Promise((resolve, reject) => {
      api.get(
        ENDPOINT.FORM.OPTIONS.CANDIDATE_ACCOUNT_REGISTRATION
          .UNDERGRADUATE_SCHOOL,
        { query, offset },
        {
          handleError: response => {
            ResponseErrorDialog({ response });
            reject();
          },
          handleSuccess: response => resolve(response),
        }
      );
    });
  };

  const getFirmOptions = (query, offset) => {
    return new Promise((resolve, reject) => {
      api.get(
        ENDPOINT.FORM.OPTIONS.CANDIDATE_ACCOUNT_REGISTRATION.FIRMS,
        { query, offset },
        {
          handleError: response => {
            ResponseErrorDialog({ response });
            reject();
          },
          handleSuccess: response => resolve(response),
        }
      );
    });
  };

  const getOfficeLocationOptions = (query, offset) => {
    return new Promise((resolve, reject) => {
      api.get(
        ENDPOINT.FORM.OPTIONS.CANDIDATE_ACCOUNT_REGISTRATION.OFFICE_LOCATIONS,
        { query, offset },
        {
          handleError: response => {
            ResponseErrorDialog({ response });
            reject();
          },
          handleSuccess: response => resolve(response),
        }
      );
    });
  };

  const changeStep = step => {
    Scroll.scroller.scrollTo('candidate-registration-form', {
      duration: 600,
      delay: 50,
      smooth: true,
    });
    GaEvent('screen_view', 'Candidate Registration Step '+step, 'Candidate Registration Step ' + step)
    setStep(step);
  };

  const { guestResume, isRecruiter, fields: values, options } = initialValues;

  return (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner candidate-registration-form">
        <div className="form__header">
          <h4 className="form__title">Create Profile</h4>
          <span className="candidate-registration-form__steps">{`Step ${step +
            1} of 4`}</span>
        </div>

        <MultistepForm
          page={step}
          validationSchema={CandidateRegistrationFormValidationSchema}
          initialValues={values}
          options={options}
          onChangeStep={changeStep}
          onSubmit={handleSubmitForm}
          getAsyncOptions={{
            getUndergraduateSchoolOptions,
            getFirmOptions,
            getOfficeLocationOptions,
          }}
          params={{ guestResume, isRecruiter }}
        >
          <MultistepFormPage>
            {renderProps => <FormStep1 {...renderProps} />}
          </MultistepFormPage>

          <MultistepFormPage>
            {renderProps => <FormStep2 {...renderProps} />}
          </MultistepFormPage>

          <MultistepFormPage>
            {renderProps => <FormStep3 {...renderProps} />}
          </MultistepFormPage>

          <MultistepFormPage>
            {renderProps => <FormStep4 {...renderProps} />}
          </MultistepFormPage>
        </MultistepForm>
      </div>
    </CSSTransition>
  );
};

CandidateRegistrationForm.propTypes = {
  onAfterSaveFormHandler: PropTypes.func.isRequired,
};

export default CandidateRegistrationForm;
